/**
 * reCAPTCHA Enterprise token generation for the unauthenticated login endpoints.
 *
 * The key is a score key, so this is invisible — there is no widget and nothing
 * for the user to solve. A token is attached to every OTP request and the
 * backend only bothers to assess it once a caller has made enough attempts to
 * look automated.
 *
 * Everything here degrades to `undefined` rather than throwing: a missing site
 * key or a blocked Google script must never stop someone logging in, and the
 * per-code attempt counter remains the real brute-force control either way.
 */

interface GrecaptchaEnterprise {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

let scriptLoader: Promise<void> | null = null;

const loadScript = (siteKey: string) => {
  if (scriptLoader) return scriptLoader;

  scriptLoader = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    // The render param is what makes execute() available; without it
    // enterprise.js only supports the checkbox widget.
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });

  return scriptLoader;
};

export const getRecaptchaToken = async (
  action: string,
): Promise<string | undefined> => {
  if (!SITE_KEY) return undefined;

  try {
    await loadScript(SITE_KEY);
    // Enterprise nests one level deeper than the classic namespace.
    const enterprise = (
      window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } }
    ).grecaptcha?.enterprise;
    if (!enterprise) return undefined;

    await new Promise<void>((resolve) => enterprise.ready(resolve));
    return await enterprise.execute(SITE_KEY, { action });
  } catch (error) {
    console.error("Could not obtain reCAPTCHA token:", error);
    return undefined;
  }
};
